import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Chip
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

const plans = {
  monthly: {
    name: 'Monthly',
    price: '29',
    features: {
      'Portfolio Score Updates': true,
      'Detailed Analytics': true,
      'Email Alerts': true,
      'API Access': false,
      'Custom Reports': false,
      'Priority Support': false,
    },
    isPopular: true
  },
  vip: {
    name: 'VIP',
    price: '99',
    features: {
      'Portfolio Score Updates': true,
      'Detailed Analytics': true,
      'Email Alerts': true,
      'API Access': true,
      'Custom Reports': true,
      'Priority Support': true,
    },
    isPopular: false
  }
};

const PlanFeature = ({ feature, included }) => (
  <ListItem disableGutters>
    <ListItemIcon sx={{ minWidth: 40 }}>
      {included ? (
        <CheckIcon color="success" />
      ) : (
        <CloseIcon color="error" />
      )}
    </ListItemIcon>
    <ListItemText 
      primary={feature}
      sx={{ 
        color: included ? 'text.primary' : 'text.disabled'
      }}
    />
  </ListItem>
);

const PricingCard = ({ plan, features, price, isPopular, onSelect }) => (
  <Paper
    elevation={isPopular ? 8 : 2}
    sx={{
      position: 'relative',
      p: 4,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 2,
      transition: 'transform 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)'
      }
    }}
  >
    {isPopular && (
      <Chip
        label="Most Popular"
        color="primary"
        sx={{
          position: 'absolute',
          top: -12,
          left: '50%',
          transform: 'translateX(-50%)'
        }}
      />
    )}

    <Box sx={{ textAlign: 'center', mb: 3 }}>
      <Typography variant="h5" component="h2" gutterBottom fontWeight="bold">
        {plan}
      </Typography>
      <Typography variant="h3" component="div" gutterBottom>
        ${price}
        <Typography variant="subtitle1" component="span" color="text.secondary">
          /mo
        </Typography>
      </Typography>
    </Box>

    <List sx={{ mb: 3, flexGrow: 1 }}>
      {Object.entries(features).map(([feature, included], index) => (
        <PlanFeature
          key={index}
          feature={feature}
          included={included}
        />
      ))}
    </List>

    <Button
      variant={isPopular ? "contained" : "outlined"}
      size="large"
      fullWidth
      onClick={onSelect}
      sx={{ mt: 'auto' }}
    >
      Select Plan
    </Button>
  </Paper>
);

export default function PremiumPlans() {
  const navigate = useNavigate();

  const handleSelectPlan = (planType) => {
    // TODO: Implement Stripe integration
    navigate('/signup/premium');
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
      <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom
          fontWeight="bold"
        >
          Choose Your Plan
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary"
          sx={{ maxWidth: 600, mx: 'auto' }}
        >
          Get detailed insights and recommendations to improve your portfolio's performance
        </Typography>
      </Box>

      <Grid container spacing={4} justifyContent="center">
        {Object.entries(plans).map(([type, plan]) => (
          <Grid item xs={12} md={6} key={type} sx={{ maxWidth: 500 }}>
            <PricingCard
              plan={plan.name}
              price={plan.price}
              features={plan.features}
              isPopular={plan.isPopular}
              onSelect={() => handleSelectPlan(type)}
            />
          </Grid>
        ))}
      </Grid>

      <Typography 
        variant="body2" 
        color="text.secondary" 
        align="center"
        sx={{ mt: 4 }}
      >
        All plans include a 14-day money-back guarantee
      </Typography>
    </Container>
  );
}